export const appNames = ["memberArea", "api"] as const;

export type AppName = (typeof appNames)[number];
export type AppEnvironment =
    | "development"
    | "remote-development"
    | "production";

export type DomainEnvironmentVariables = {
    readonly VITE_MEMBER_AREA_URL?: string;
    readonly VITE_API_URL?: string;
};

export type DomainConfig = Readonly<Record<AppName, string>>;

const supportedEnvironments: readonly AppEnvironment[] = [
    "development",
    "remote-development",
    "production",
];

const environmentVariableByApp: Record<
    AppName,
    keyof DomainEnvironmentVariables
> = {
    memberArea: "VITE_MEMBER_AREA_URL",
    api: "VITE_API_URL",
};

const isAppName = (appName: string): appName is AppName =>
    appNames.includes(appName as AppName);

const isSupportedEnvironment = (
    environment: string
): environment is AppEnvironment =>
    supportedEnvironments.includes(environment as AppEnvironment);

const normalizeDomain = (
    appName: AppName,
    environment: AppEnvironment,
    rawDomain: string | undefined
): string => {
    if (!rawDomain?.trim()) {
        throw new Error(
            `Missing domain for "${appName}" in environment "${environment}" (${environmentVariableByApp[appName]}).`
        );
    }

    let parsedDomain: URL;

    try {
        parsedDomain = new URL(rawDomain);
    } catch {
        throw new Error(
            `Invalid domain for "${appName}" in environment "${environment}": "${rawDomain}".`
        );
    }

    if (!["http:", "https:"].includes(parsedDomain.protocol)) {
        throw new Error(
            `Invalid protocol for "${appName}" in environment "${environment}": "${parsedDomain.protocol}".`
        );
    }

    return parsedDomain.toString().replace(/\/+$/, "");
};

export const resolveDomainConfig = (
    environment: string,
    env: DomainEnvironmentVariables
): DomainConfig => {
    if (!isSupportedEnvironment(environment)) {
        throw new Error(
            `Unsupported application environment "${environment}". Expected one of: ${supportedEnvironments.join(", ")}.`
        );
    }

    return Object.freeze({
        memberArea: normalizeDomain(
            "memberArea",
            environment,
            env.VITE_MEMBER_AREA_URL
        ),
        api: normalizeDomain("api", environment, env.VITE_API_URL),
    });
};

const domains = resolveDomainConfig(import.meta.env.MODE, import.meta.env);

export const getDomain = (appName: AppName): string => {
    if (!isAppName(appName)) {
        throw new Error(
            `Unknown application target "${appName}". Expected one of: ${appNames.join(", ")}.`
        );
    }

    return domains[appName];
};

export const buildUrl = (appName: AppName, path = ""): string => {
    const domain = getDomain(appName);

    if (!path) {
        return domain;
    }

    const normalizedPath = path.replace(/^\/+/, "");
    return new URL(normalizedPath, `${domain}/`).toString();
};
